import { getLanguageCode, getLanguageFromLocalStorage } from "utils";

import { LANGUAGE_CODE } from "consts";

const IUBENDA_CONFIG = Object.freeze({
  siteId: 2241489,
  policyIds: {
    [LANGUAGE_CODE.GERMAN]: { cookiePolicyId: 24461025 },
    [LANGUAGE_CODE.ENGLISH]: { cookiePolicyId: 38525799 },
    [LANGUAGE_CODE.ENGLISH_GB]: { cookiePolicyId: 38525799 },
    [LANGUAGE_CODE.ENGLISH_US]: { cookiePolicyId: 75266411 },
    [LANGUAGE_CODE.POLISH]: { cookiePolicyId: 91530343 },
    [LANGUAGE_CODE.CZECH]: { cookiePolicyId: 94797914 },
    [LANGUAGE_CODE.SLOVAK]: { cookiePolicyId: 20390928 },
  },
});

// the language code used by iubenda, not the language code used in our codebase
const getIubendaLanguageCode = (language: LANGUAGE_CODE) => {
  switch (language) {
    case LANGUAGE_CODE.GERMAN:
      return "de";
    case LANGUAGE_CODE.ENGLISH_US:
      return "en";
    case LANGUAGE_CODE.ENGLISH:
    case LANGUAGE_CODE.ENGLISH_GB:
      return "en-GB";
    case LANGUAGE_CODE.POLISH:
      return "pl";
    case LANGUAGE_CODE.CZECH:
      return "cs";
    case LANGUAGE_CODE.SLOVAK:
      return "sk";
    default:
      return "de";
  }
};

export const getIubendaPolicyId = (language: LANGUAGE_CODE): number => {
  const cookiesLanguage = getLanguageCode(language);

  if (cookiesLanguage in IUBENDA_CONFIG.policyIds) {
    return IUBENDA_CONFIG.policyIds[cookiesLanguage].cookiePolicyId;
  }

  return IUBENDA_CONFIG.policyIds[LANGUAGE_CODE.GERMAN].cookiePolicyId;
};

export const initCookieBanner = () => {
  const localStorageLanguage = getLanguageFromLocalStorage();
  const cookiesLanguage = getLanguageCode(localStorageLanguage);
  const cookiesPolicyId = getIubendaPolicyId(localStorageLanguage);
  const iubendaLanguageCode = getIubendaLanguageCode(cookiesLanguage);

  // If Iubenda is already loaded with a different language, reload the page to reinitialize
  const currentIubendaLanguage = window._iub?.csConfiguration?.language;
  if (currentIubendaLanguage && currentIubendaLanguage !== iubendaLanguageCode) {
    window.location.reload();
    return;
  }

  const iubCsConfiguration = {
    askConsentAtCookiePolicyUpdate: true,
    countryDetection: false, // use explicit language instead of geo override
    enableFadp: true,
    enableUspr: true,
    floatingPreferencesButtonDisplay: "bottom-left",
    language: iubendaLanguageCode,
    lang: iubendaLanguageCode, // explicit for Iubenda
    logViaAlert: true,
    perPurposeConsent: true,
    siteId: IUBENDA_CONFIG.siteId,
    usPreferencesWidgetDisplay: false,
    whitelabel: false,
    cookiePolicyId: cookiesPolicyId,
    reloadOnConsent: true,
    banner: {
      acceptButtonCaptionColor: "#0B0B0B",
      acceptButtonColor: "#ECF86E",
      acceptButtonDisplay: true,
      backgroundColor: "#E6E1E1",
      closeButtonDisplay: false,
      customizeButtonCaptionColor: "#030303",
      customizeButtonColor: "#DED8D8",
      customizeButtonDisplay: true,
      explicitWithdrawal: true,
      listPurposes: true,
      logo: null,
      linksColor: "#0C0B0B",
      ownerName: "Klim",
      position: "bottom",
      rejectButtonCaptionColor: "#0F0F0F",
      rejectButtonColor: "#DED8D8",
      rejectButtonDisplay: true,
      showTitle: false,
      textColor: "#050505",
    },
  };

  const iubCsLangConfiguration = Object.fromEntries(
    Object.entries(IUBENDA_CONFIG.policyIds).map(([language, policyId]) => [
      getIubendaLanguageCode(language as LANGUAGE_CODE),
      { cookiePolicyId: policyId.cookiePolicyId },
    ])
  );

  const scriptExists = (src: string) => !!document.querySelector(`script[src="${src}"]`);

  // add the Iubenda configuration script
  const existingConfigScript = document.querySelector('script[data-config="iubCsConfiguration"]');
  const configScript = document.createElement("script");
  configScript.type = "text/javascript";
  configScript.text = `
    var _iub = _iub || [];
    _iub.csConfiguration = ${JSON.stringify(iubCsConfiguration)};
    _iub.csLangConfiguration = ${JSON.stringify(iubCsLangConfiguration)};
  `;
  configScript.setAttribute("data-config", "iubCsConfiguration");

  // if the config script already exists, we replace the old script with the new one created with the update config, otherwise we add the script to the DOM
  if (existingConfigScript) {
    existingConfigScript.parentNode?.replaceChild(configScript, existingConfigScript);
  } else {
    document.head.appendChild(configScript);
  }

  // add the Iubenda auto blocking script
  const autoBlockingScriptSrc = `https://cs.iubenda.com/autoblocking/${IUBENDA_CONFIG.siteId}.js`;
  if (!scriptExists(autoBlockingScriptSrc)) {
    const autoBlockingScript = document.createElement("script");
    autoBlockingScript.type = "text/javascript";
    autoBlockingScript.src = autoBlockingScriptSrc;
    document.head.appendChild(autoBlockingScript);
  }

  // add the Iubenda GPP stub script
  const stubScriptSrc = "//cdn.iubenda.com/cs/gpp/stub.js";
  if (!scriptExists(stubScriptSrc)) {
    const stubScript = document.createElement("script");
    stubScript.type = "text/javascript";
    stubScript.src = stubScriptSrc;
    document.head.appendChild(stubScript);
  }

  // add the Iubenda main script
  const mainScriptSrc = "//cdn.iubenda.com/cs/iubenda_cs.js";
  if (!scriptExists(mainScriptSrc)) {
    const mainScript = document.createElement("script");
    mainScript.type = "text/javascript";
    mainScript.src = mainScriptSrc;
    mainScript.charset = "UTF-8";
    mainScript.async = true;

    // add data-testid to the accept button for e2e tests
    mainScript.onload = () => {
      let attempts = 0;

      // we have to make sure the button is loaded in the DOM, so we set an interval to check every 100ms
      const checkForButton = setInterval(() => {
        const acceptButton = document.querySelector(".iubenda-cs-accept-btn.iubenda-cs-btn-primary");
        if (acceptButton) {
          acceptButton.setAttribute("data-testid", "cookies-accept-all-button");

          // stop checking once the button is found and modified
          clearInterval(checkForButton);
        } else if (attempts >= 100) {
          clearInterval(checkForButton);
        }
        attempts += 1;
      }, 50);
    };

    document.head.appendChild(mainScript);
  }
};

export const toggleCookiesPreferencesButtonVisibility = (visible: boolean) => {
  let attempts = 0;

  const checkButtonLoaded = () => {
    const iubendaCsPreferencesLinkButton = document.querySelector(".iubenda-tp-btn.iubenda-cs-preferences-link") as HTMLElement;
    // Hide any existing widget in the DOM when not on sign-in/sign-up pages.
    const usPrivacyWidget = document.querySelector(".iubenda-uspr-widget") as HTMLElement;

    if (iubendaCsPreferencesLinkButton) {
      iubendaCsPreferencesLinkButton.style.setProperty("display", visible ? "inline-block" : "none", "important");
    }

    if (usPrivacyWidget) {
      usPrivacyWidget.style.setProperty("display", visible ? "block" : "none", "important");
    }

    if (!iubendaCsPreferencesLinkButton && attempts < 50) {
      attempts += 1;
      setTimeout(checkButtonLoaded, 50);
    }
  };

  checkButtonLoaded();
};

const getCookieValue = (cookiePolicyId: number) => {
  const cookiesList = Object.fromEntries(
    document.cookie.split("; ").map((keyVal) => {
      const [key, ...val] = keyVal.split("=");
      return [decodeURIComponent(key), decodeURIComponent(val.join("="))];
    })
  );
  const cookieName = `_iub_cs-${cookiePolicyId}`;
  const cookieValue = cookiesList[cookieName];

  if (!cookieValue) return null;

  // try to parse the decoded cookie string, if this errors out, return the string itself
  try {
    return JSON.parse(cookieValue);
  } catch {
    return cookieValue;
  }
};

export const checkCookieConsent = (): boolean => {
  const localStorageLanguage = getLanguageFromLocalStorage();
  const policyId = getIubendaPolicyId(localStorageLanguage);
  const cookieValue = getCookieValue(policyId);

  if (!cookieValue?.purposes || cookieValue?.id !== policyId) return false;
  return Object.values(cookieValue.purposes).every((purpose) => purpose === true);
};

export const acceptAllCookies = () => {
  const isIubendaLoaded = () => typeof window._iub?.cs?.api?.acceptAll === "function";

  const waitForIubenda = (interval: number, maxAttempts: number, callback: () => void) => {
    let attempts = 0;

    const checkIfIubendaIsLoaded = () => {
      if (isIubendaLoaded()) {
        callback();
      } else if (attempts < maxAttempts) {
        attempts += 1;
        setTimeout(checkIfIubendaIsLoaded, interval);
      }
    };

    checkIfIubendaIsLoaded();
  };

  if (isIubendaLoaded()) {
    window._iub.cs.api.acceptAll();
  } else {
    waitForIubenda(50, 50, () => {
      window._iub.cs.api.acceptAll();
    });
  }
};
